import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { destroy, get, put } from '../utils';
import type { OrganisationMfaPolicy, UserMfaStatus } from '../../../types/Mfa';

enum QueryKeys {
  UserMfaStatus = 'UserMfaStatus',
}

export const userMfaStatusQueryKey = (userId: string): [string, string] => [QueryKeys.UserMfaStatus, userId];

/**
 * GET /microservice/user/:id/mfa. No error toast on purpose: an API that predates the route answers 404 on
 * every user page, and the row renders "Unavailable" itself.
 */
export function useQueryGetUserMfaStatus(userId: string): UseQueryResult<UserMfaStatus> {
  return useQuery(userMfaStatusQueryKey(userId), () => get<UserMfaStatus>(`user/${userId}/mfa`), {
    enabled: Boolean(userId),
  });
}

/** DELETE /microservice/user/:id/mfa: revokes every session, then removes the authenticator and recovery codes. */
export function useResetUserMfaMutation(userId: string) {
  const queryClient = useQueryClient();
  return useMutation(
    async () => {
      await destroy(`user/${userId}/mfa`);
    },
    {
      // onSettled, not onSuccess: the API revokes the sessions before it removes the method, so a failure part-way
      // leaves the status half-changed and the cached copy stale.
      onSettled: () => {
        queryClient.invalidateQueries(userMfaStatusQueryKey(userId), {}, { cancelRefetch: true });
      },
    }
  );
}

/**
 * PUT /microservice/organisation/:id/mfa-policy: the staff override, never gated by ENABLE_MFA_POLICY. Returns the
 * policy with member counts. The organisation page holds its organisation in local state, so the caller applies
 * the returned mfaRequired itself; there is no organisation query to invalidate.
 */
export function useSetOrganisationMfaPolicyMutation(organisationId: string) {
  return useMutation((mfaRequired: boolean) =>
    put<OrganisationMfaPolicy>(`organisation/${organisationId}/mfa-policy`, { mfaRequired })
  );
}
